import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getDayEffectiveEndPosition,
  hasHotelLocationConflict,
} from '@koob/utils';
import { useDays, useTripStore } from '@koob/trip-designer';
import { useSnack } from '../../../hooks';

/**
 * Once the trip and its hotels are in the store, tells the user (once) when a
 * hotel sits away from where its day ends.
 */
export function useHotelConflictNotice() {
  const { t } = useTranslation('tripDesigner');
  const { notify } = useSnack();
  const days = useDays();
  const experiencesMap = useTripStore(state => state.experiences);
  const hotelsMap = useTripStore(state => state.hotels);
  const conflictCheckedRef = useRef(false);

  useEffect(() => {
    if (
      days &&
      days.length > 0 &&
      experiencesMap &&
      Object.keys(experiencesMap).length > 0 &&
      !conflictCheckedRef.current
    ) {
      const hotelDataReady = days.every(
        day => !day?.hotelId || hotelsMap?.[day.hotelId],
      );

      if (!hotelDataReady) return;

      const hasConflicts = days.some((day, dayIndex) => {
        if (!day?.hotelId) return false;
        const hotel = hotelsMap?.[day.hotelId];
        const exp = experiencesMap?.[day.experienceId];
        const prog = exp?.programs?.[(day.experienceDayIndex ?? 1) - 1];

        return hasHotelLocationConflict({
          hotel,
          program: prog,
          // KD-3635 — the toast follows where the day actually ends, like the
          // badge does (KD-3548).
          endPosition: getDayEffectiveEndPosition({
            days,
            dayIndex,
            experiencesMap,
            experience: exp,
            program: prog,
          }),
        });
      });

      if (hasConflicts) {
        notify(t('compose.hotelLocationMismatch'), { type: 'error' });
      }

      conflictCheckedRef.current = true;
    }
  }, [days, experiencesMap, hotelsMap, notify, t]);
}
